<template lang="pug">
.contract-custom-fields-data.card
  .card-header {{ $t('title') }}
    .fa-pull-right
      .btn-group.btn-group-sm
        button.btn.btn-primary.animated.fadeIn(
          v-if           = '!editing'
          @click.prevent = 'toggleEditing()'
        ) {{ $t("common:edit") }}
  .card-body
    form.form-horizontal(
      novalidate
      @submit.prevent = 'onSubmit'
    )
      template(v-if = 'editing')
        .row
          .col
            .input-group.input-group-sm.mb-2
              fi-select(
                v-model = 'selectedField'
                :options = 'optionsFromCustomFields'
                required
                sm
              )
              .input-group-append
                button.btn.btn-primary(
                  :disabled      = 'saving || !optionsFromCustomFields.length'
                  @click.prevent = 'addField'
                ) {{ $t('common:add') }}
                  i.fa.fa-spinner.fa-pulse.ml-1(v-if = 'saving')
        fi-custom-field-item(
          v-for     = '(customField, index) in customFieldsData'
          :key      = 'index'
          v-model   = 'customField.customFieldValue'
          :options  = 'selectedFields[customField.productCustomFieldId].customField'
          :disabled = '!selectedFields[customField.productCustomFieldId].enabled'
          :required = 'customField.required'
          editing
          @remove   = 'removeField(index)'
        )
        button.btn.btn-primary(
          :disabled = 'saving || checkRequired'
          type      = 'submit'
        )
          |{{ $t('common:save') }}
          i.fa.fa-spinner.fa-pulse.ml-1(v-if = 'saving')
        | &nbsp;
        button.btn.btn-secondary(
          :disabled      = 'saving'
          @click.prevent = 'toggleEditing(false)'
        ) {{ $t('common:cancel') }}
      template(v-else)
        fi-custom-field-item(
          v-for     = 'field in customFieldsData'
          :key      = 'field.id'
          :required = 'field.required'
          :options  = 'selectedFields[field.productCustomFieldId].customField'
          :value    = 'field.customFieldValue'
        )
</template>

<script>
import FiFormField from '@/components/FiFormField'
import FiSelect from '@/components/FiSelect'
import FiCustomFieldItem from '@/components/FiCustomFieldItem'
import FiDataField from '@/components/FiDataField'
import { sortClassifiers } from '@/helpers'

export default {
  name: 'contract-custom-fields-data',
  components: { FiDataField, FiCustomFieldItem, FiSelect, FiFormField },
  props: {
    customFields: {
      type: Array,
      default: () => []
    },
    loader: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    editing: false,
    selectedField: '',
    customFieldsData: []
  }),

  computed: {
    saving () {
      return this.$vueLoading.isLoading(this.loader)
    },
    checkRequired () {
      return this.customFieldsData.find(field => field.required && !field.customFieldValue)
    },
    optionsFromCustomFields () {
      return sortClassifiers(this.options.filter(({ enabled, required }) => enabled && !required)
        .map(({ id, customField, ...field }) => ({ ...field, ...customField, text: customField.name, value: id }))
        .filter(({ value }) => !Object.keys(this.selectedFields).includes(`${value}`)))
    },
    selectedFields () {
      return this.customFieldsData
        .reduce((res, { productCustomFieldId }) => ({ ...res, [productCustomFieldId]: this.options.find(({ id }) => id === productCustomFieldId) }), {})
    }
  },

  created () {
    this.resetData()
  },

  methods: {
    toggleEditing (state = !this.editing) {
      this.resetData()
      this.editing = state
    },
    addField () {
      const [defaultValue] = this.optionsFromCustomFields.find(({ value }) => value === this.selectedField)
        ?.defaultValues?.filter(({ isEnabled }) => isEnabled)
      this.customFieldsData.push({
        productCustomFieldId: this.selectedField,
        customFieldValue: defaultValue?.classifierValue
      })
      this.selectedField = ''
    },
    removeField (index) {
      this.customFieldsData.splice(index, 1)
    },
    resetData () {
      Object.assign(this.$data, this.$options.data.call(this, this))
      this.customFieldsData = this.customFields.map(({ customFieldValue, productCustomFieldId, required }) => ({
        productCustomFieldId,
        customFieldValue,
        required
      }))
      this.customFieldsData.forEach(field => {
        this.options.forEach(option => {
          if (field.productCustomFieldId === option.id) {
            field.required = option.required
          }
        })
      })
      this.customFieldsData = [...this.customFieldsData, ...this.options.filter(({ enabled, required }) => enabled && required)
        .filter((field) => !this.customFieldsData.some((customField) => customField.productCustomFieldId === field.id))
        .map(({ id, required }) => ({ productCustomFieldId: id, customFieldValue: undefined, required }))]
    },
    onSubmit () {
      this.customFieldsData.forEach(field => delete field.required)
      this.$emit('submit', {
        data: this.customFieldsData.filter(({ productCustomFieldId }) => this.selectedFields[productCustomFieldId].enabled),
        done: () => {
          this.resetData()
        }
      })
    }
  }
}
</script>

  <i18n>
  en:
    title: "Custom fields"
  et:
    title: "Custom fields"
  ru:
    title: "Custom fields"
  </i18n>
